import React, { useState } from 'react';
import { Check } from 'lucide-react';
import StepHeading from './StepHeading';
import { noAutofill } from '../noAutofill';

interface Step3Props {
  productNames: string[];
  selectedName: string;
  setSelectedName: (name: string) => void;
  handleStep3: () => void;
  loading: boolean;
  isGeneratingNames: boolean;
}

const STRATEGY_LABELS = ['Evocative', 'Coined', 'Plainspoken'];

const Step3: React.FC<Step3Props> = ({
  productNames,
  selectedName,
  setSelectedName,
  handleStep3,
  loading,
  isGeneratingNames,
}) => {
  const [customName, setCustomName] = useState('');
  const [isCustom, setIsCustom] = useState(false);

  const chooseSuggestion = (name: string) => {
    setIsCustom(false);
    setCustomName('');
    setSelectedName(name);
  };

  const chooseCustom = (value: string) => {
    setIsCustom(true);
    setCustomName(value);
    setSelectedName(value.trim() ? value : '');
  };

  return (
    <div className="ed-enter">
      <StepHeading
        index={3}
        kicker="Naming"
        title="Every product deserves a name"
        accent="people repeat."
        lede="Three directions, each written to a different strategy. Take one as it is, or write your own."
      />

      <div className="ed-block">
        <div className="ed-rows" role="radiogroup" aria-label="Product name">
          {[0, 1, 2].map((index) => {
            if (isGeneratingNames || !productNames[index]) {
              return (
                <div key={index} className="ed-skeleton" style={{ borderTop: 0 }}>
                  Writing name {index + 1}
                </div>
              );
            }

            const name = productNames[index];
            const isSelected = !isCustom && selectedName === name;

            return (
              <div
                key={index}
                role="radio"
                aria-checked={isSelected}
                tabIndex={0}
                className={`ed-row${isSelected ? ' is-selected' : ''}`}
                onClick={() => chooseSuggestion(name)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    chooseSuggestion(name);
                  }
                }}
              >
                <span className="ed-row-index">{String(index + 1).padStart(2, '0')}</span>
                <div className="ed-row-main">
                  <h3 className="ed-row-title">{name}</h3>
                  <p className="ed-row-sub">{STRATEGY_LABELS[index]}</p>
                </div>
                {isSelected && (
                  <span className="ed-mark" aria-hidden="true">
                    <Check />
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="ed-block ed-block--sheet">
        <label className="ed-label" htmlFor="custom-name">
          Or name it yourself
        </label>
        <input
          id="custom-name"
          type="text"
          className="ed-input ed-input--display"
          value={customName}
          onChange={(e) => chooseCustom(e.target.value)}
          onFocus={() => chooseCustom(customName)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && selectedName && !loading) handleStep3();
          }}
          placeholder="Your own name"
          {...noAutofill}
        />
      </div>
    </div>
  );
};

export default Step3;
